import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Divider,
  DrawerBody,
  DrawerHeader,
  DrawerHeaderTitle,
  Field,
  Input,
  Link,
  OverlayDrawer,
  Spinner,
  Text,
  Toast,
  Toaster,
  ToastTitle,
  tokens,
  useId,
  useToastController,
  type InputProps,
} from "@fluentui/react-components";
import { Delete20Regular, Dismiss24Regular } from "@fluentui/react-icons";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";

import { removeEmail } from "@/api/emails";
import { StatusBadge } from "@/components/StatusBadge";
import { DocumentRow } from "@/features/documents/DocumentRow";
import { TradeMatchSuggestions } from "@/features/documents/TradeMatchSuggestions";
import { useDocumentTypes } from "@/features/documents/useDocumentTypes";
import { useEmailDetail } from "@/features/documents/useEmailDetail";
import { useUpdateEmail } from "@/features/documents/useUpdateEmail";
import { ApiError } from "@/lib/apiClient";
import { ParcelSelector } from "@/features/review/ParcelSelector";
import { ParcelSummaryRow } from "@/features/review/ParcelSummaryRow";
import { useSelectionStore } from "@/store/selectionStore";
import { isArchiveOnly } from "@/types/domain";

function describeError(err: unknown): string {
  if (err instanceof ApiError) {
    const body = err.body;
    if (body && typeof body === "object" && "detail" in body) {
      const detail = (body as Record<string, unknown>).detail;
      if (typeof detail === "string") return detail;
    }
    return err.message;
  }
  return err instanceof Error ? err.message : "Remove failed";
}

const DATE_TIME_FMT = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "numeric",
  year: "numeric",
  hour: "numeric",
  minute: "numeric",
  second: "numeric",
  hour12: true,
});

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  try {
    return DATE_TIME_FMT.format(new Date(iso));
  } catch {
    return iso;
  }
}

function MetadataRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "120px 1fr",
        gap: 8,
        padding: "4px 0",
      }}
    >
      <Text size={200} style={{ color: tokens.colorNeutralForeground2 }}>
        {label}
      </Text>
      <div>{children}</div>
    </div>
  );
}

export function EmailDetailDrawer() {
  const selectedId = useSelectionStore((s) => s.selectedEmailId);
  const close = useSelectionStore((s) => s.close);
  const { data, isLoading, isError, error } = useEmailDetail(selectedId);
  const updateEmail = useUpdateEmail();
  const toasterId = useId("email-drawer-toaster");
  const { dispatchToast } = useToastController(toasterId);
  const qc = useQueryClient();
  const [parcelSelectorOpen, setParcelSelectorOpen] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);

  const removeMutation = useMutation({
    mutationFn: () => {
      if (!selectedId) throw new Error("No email selected");
      return removeEmail(selectedId);
    },
    onSuccess: (result) => {
      setConfirmRemove(false);
      void qc.invalidateQueries({ queryKey: ["emails"] });
      void qc.invalidateQueries({ queryKey: ["email"] });
      dispatchToast(
        <Toast>
          <ToastTitle>
            Email removed · {result.documentsRemoved} document(s) discarded
            {result.documentsSkipped > 0
              ? ` · ${result.documentsSkipped} already removed`
              : ""}
          </ToastTitle>
        </Toast>,
        { intent: "success" },
      );
      close();
    },
    onError: (err) => {
      dispatchToast(
        <Toast>
          <ToastTitle>Remove failed: {describeError(err)}</ToastTitle>
        </Toast>,
        { intent: "error" },
      );
    },
  });

  const hasApprovedDoc = useMemo(
    () => (data?.documents ?? []).some((d) => d.approvalStatus === "approved"),
    [data?.documents],
  );

  // Local buffer so typing feels responsive; commit on explicit Update.
  const [tradeKeyDraft, setTradeKeyDraft] = useState("");
  // Fuzzy-match dropdown is sticky: shown on first focus while the email
  // is uncategorized, then stays open until the operator dismisses it or
  // a trade_key gets committed. Blur / scroll / autofill clicks never
  // close it (they used to, which was bug-prone).
  const [matchesVisible, setMatchesVisible] = useState(false);

  useEffect(() => {
    setTradeKeyDraft(data?.tradeKey ?? "");
    setMatchesVisible(false);
  }, [data?.tradeKey, selectedId]);

  const onTradeKeyChange: InputProps["onChange"] = (_, d) => {
    setTradeKeyDraft(d.value);
  };

  const applyTradeKey = () => {
    if (!selectedId) return;
    const next = tradeKeyDraft.trim();
    const current = data?.tradeKey ?? "";
    if (next === current) return;
    updateEmail.mutate({ id: selectedId, tradeKey: next === "" ? null : next });
  };

  const tradeKeyDirty = (tradeKeyDraft.trim() || null) !== (data?.tradeKey ?? null);

  const { data: documentTypes } = useDocumentTypes();

  const { reviewDocs, archiveDocs } = useMemo(() => {
    if (!data) return { reviewDocs: [], archiveDocs: [] };
    const review: typeof data.documents = [];
    const archive: typeof data.documents = [];
    for (const doc of data.documents) {
      if (isArchiveOnly(doc.documentType, documentTypes)) {
        archive.push(doc);
      } else {
        review.push(doc);
      }
    }
    return { reviewDocs: review, archiveDocs: archive };
  }, [data, documentTypes]);

  return (
    <OverlayDrawer
      open={selectedId !== null}
      onOpenChange={(_, payload) => {
        if (!payload.open) close();
      }}
      position="end"
      size="medium"
    >
      <DrawerHeader>
        <DrawerHeaderTitle
          action={
            <Button
              appearance="subtle"
              aria-label="Close"
              icon={<Dismiss24Regular />}
              onClick={close}
            />
          }
        >
          Email details
        </DrawerHeaderTitle>
      </DrawerHeader>
      <DrawerBody>
        {isLoading ? (
          <div style={{ padding: 32, textAlign: "center" }}>
            <Spinner label="Loading…" />
          </div>
        ) : isError ? (
          <Text style={{ color: tokens.colorPaletteRedForeground1 }}>
            {error instanceof Error ? error.message : "Failed to load email"}
          </Text>
        ) : data ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
              <Text weight="semibold" size={500} style={{ flex: 1 }}>
                {data.subject || "(no subject)"}
              </Text>
              <StatusBadge status={data.aggregateStatus} />
            </div>

            <section>
              <MetadataRow label="From">
                <Text>{data.from || "(unknown)"}</Text>
              </MetadataRow>
              {data.to ? (
                <MetadataRow label="To">
                  <Text>{data.to}</Text>
                </MetadataRow>
              ) : null}
              <MetadataRow label="Received">
                <Text>{formatDate(data.receivedAt)}</Text>
              </MetadataRow>
              {data.emailLink ? (
                <MetadataRow label="Email">
                  <Link href={data.emailLink} target="_blank" rel="noreferrer">
                    Open in Outlook
                  </Link>
                </MetadataRow>
              ) : null}
              {data.folderLink ? (
                <MetadataRow label="Folder">
                  <Link href={data.folderLink} target="_blank" rel="noreferrer">
                    Open in SharePoint
                  </Link>
                </MetadataRow>
              ) : null}
              <MetadataRow label="Status counts">
                <Text size={200}>
                  {data.statusCounts.pendingReview} pending ·{" "}
                  {data.statusCounts.approved} approved ·{" "}
                  {data.statusCounts.archived + data.statusCounts.removed}{" "}
                  discarded · {data.statusCounts.failed} failed
                </Text>
              </MetadataRow>
            </section>

            <Divider />

            <section
              style={{ display: "flex", flexDirection: "column", gap: 8 }}
            >
              <Field
                label="Trade key"
                hint="Applying moves originals and any docs still inheriting from this email."
                validationState={
                  updateEmail.isError
                    ? "error"
                    : updateEmail.isSuccess && !tradeKeyDirty
                      ? "success"
                      : "none"
                }
                validationMessage={
                  updateEmail.isError
                    ? (updateEmail.error instanceof Error
                        ? updateEmail.error.message
                        : "Update failed")
                    : updateEmail.isSuccess && !tradeKeyDirty
                      ? "Updated"
                      : undefined
                }
              >
                <div style={{ display: "flex", gap: 8 }}>
                  <Input
                    value={tradeKeyDraft}
                    onChange={onTradeKeyChange}
                    onFocus={() => {
                      // Only auto-open the suggestions for uncategorized
                      // emails — operators editing a known key shouldn't
                      // get a dropdown.
                      if ((data?.tradeKey ?? null) === null) {
                        setMatchesVisible(true);
                      }
                    }}
                    // Suppress Chrome's address-book autofill — it overlays
                    // the suggestions panel and steals clicks. Random name
                    // + autocomplete="off" is the combination Chrome
                    // currently honours.
                    name="trade-key-fuzzy-search"
                    autoComplete="off"
                    placeholder="Not set"
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && tradeKeyDirty) applyTradeKey();
                    }}
                    style={{ flex: 1 }}
                  />
                  <Button
                    appearance="primary"
                    onClick={applyTradeKey}
                    disabled={!tradeKeyDirty || updateEmail.isPending}
                  >
                    {updateEmail.isPending ? "Updating…" : "Update"}
                  </Button>
                </div>
              </Field>
              {selectedId && matchesVisible && (data?.tradeKey ?? null) === null ? (
                <TradeMatchSuggestions
                  emailId={selectedId}
                  disabled={updateEmail.isPending}
                  onClose={() => setMatchesVisible(false)}
                  onPick={(tradeKey) => {
                    setTradeKeyDraft(tradeKey);
                    setMatchesVisible(false);
                    updateEmail.mutate({ id: selectedId, tradeKey });
                  }}
                />
              ) : null}
              <ParcelSummaryRow
                emailId={data.id}
                selectedParcelNumber={data.selectedParcelNumber}
                candidateCount={data.parcelCandidates.length}
                onChange={() => setParcelSelectorOpen(true)}
              />
            </section>

            <Divider />

            {reviewDocs.length > 0 && (
              <section style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <Text weight="semibold">
                  Documents for review ({reviewDocs.length})
                </Text>
                {reviewDocs.map((doc) => (
                  <DocumentRow key={doc.id} doc={doc} />
                ))}
              </section>
            )}

            {archiveDocs.length > 0 && (
              <>
                {reviewDocs.length > 0 && <Divider />}
                <section style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <Text weight="semibold"
                    style={{ color: tokens.colorNeutralForeground2 }}
                  >
                    Archive only ({archiveDocs.length})
                  </Text>
                  {archiveDocs.map((doc) => (
                    <DocumentRow key={doc.id} doc={doc} />
                  ))}
                </section>
              </>
            )}

            {/* Destructive action parked at the very bottom-right so a
                misclick from "close drawer" can't reach it. */}
            <div
              style={{
                marginTop: 16,
                paddingTop: 12,
                borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <Button
                appearance="subtle"
                icon={<Delete20Regular />}
                onClick={() => setConfirmRemove(true)}
                disabled={hasApprovedDoc || removeMutation.isPending}
                title={
                  hasApprovedDoc
                    ? "Cannot remove — at least one document is already approved"
                    : "Discard this email and every underlying document"
                }
              >
                Remove email
              </Button>
            </div>
          </div>
        ) : null}
      </DrawerBody>
      {data && parcelSelectorOpen ? (
        <ParcelSelector
          emailId={data.id}
          documentId={null}
          selectedParcelNumber={data.selectedParcelNumber}
          parcelCandidates={data.parcelCandidates}
          parcelFetchStatus={data.parcelFetchStatus}
          open={parcelSelectorOpen}
          onClose={() => setParcelSelectorOpen(false)}
          toasterId={toasterId}
        />
      ) : null}
      <Dialog
        open={confirmRemove}
        onOpenChange={(_, d) => {
          if (!d.open) setConfirmRemove(false);
        }}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Remove email and all its documents?</DialogTitle>
            <DialogContent>
              This permanently deletes every underlying document's SharePoint
              folder, the email's original attachments, and the tracking-list
              rows. Postgres state will record the removal for audit. This
              cannot be undone.
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setConfirmRemove(false)}>Cancel</Button>
              <Button
                appearance="primary"
                disabled={removeMutation.isPending}
                onClick={() => removeMutation.mutate()}
              >
                {removeMutation.isPending ? "Removing…" : "Remove email"}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
      <Toaster toasterId={toasterId} position="top-end" />
    </OverlayDrawer>
  );
}
